import React, { useEffect, useState } from 'react';
import { useSwipeable } from 'react-swipeable';
import { getComponentClasses } from '@99designs/frontend-components/utils/app';
import CarouselItems from './CarouselItems';
import CarouselTriggers from './CarouselTriggers';

interface Props {
    items: React.ReactNodeArray;
    variations?: string[];
    showTriggers?: boolean;
    showTriggersOnHover?: boolean;
    autoPlay?: boolean;
    intervalTime?: number;
    animation?: 'slide-in' | 'fade-in';
    limit?: number;
    disableSwipeToNavigate?: boolean;
}

export default function Carousel({
    items,
    variations = [],
    showTriggers = false,
    showTriggersOnHover = false,
    autoPlay = false,
    intervalTime = 5000,
    animation = 'slide-in',
    limit = 5,
    disableSwipeToNavigate = false,
}: Props) {
    const classVariations = variations.concat([
        getTriggerVariation(showTriggers, showTriggersOnHover),
        animation,
    ]);

    const limitedItems = items.slice(0, limit);
    const className = getComponentClasses('carousel', classVariations);
    const [activeStep, setActiveStep] = useState(0);
    const [canAutoPlay, setCanAutoPlay] = useState(true);
    const lastStep = limitedItems.length - 1;

    useEffect(() => {
        if (autoPlay && canAutoPlay) {
            const interval = setInterval(() => {
                goToNextStep();
            }, intervalTime);

            return () => clearInterval(interval);
        }
    }, [activeStep, canAutoPlay]);

    const goToNextStep = () => {
        if (activeStep == lastStep) {
            setActiveStep(0);
        } else {
            setActiveStep(activeStep + 1);
        }
    };

    const goToPrevStep = () => {
        if (activeStep == 0) {
            setActiveStep(lastStep);
        } else {
            setActiveStep(activeStep - 1);
        }
    };

    const swipeHandlers = useSwipeable({
        onSwipedLeft: goToNextStep,
        onSwipedRight: goToPrevStep,
        preventDefaultTouchmoveEvent: true,
        trackMouse: false,
        trackTouch: true,
    });

    return (
        <div
            className={className}
            data-testid="carousel"
            {...(disableSwipeToNavigate ? [] : swipeHandlers)}
        >
            <div className="carousel__content">
                {shouldHaveTriggers(showTriggers, showTriggersOnHover, limitedItems.length) && (
                    <CarouselTriggers
                        handlePrevClick={() => {
                            setCanAutoPlay(false);
                            goToPrevStep();
                        }}
                        handleNextClick={() => {
                            setCanAutoPlay(false);
                            goToNextStep();
                        }}
                    />
                )}
                <div className="carousel__content__viewport">
                    <CarouselItems
                        activeStep={activeStep}
                        items={limitedItems}
                        animation={animation}
                    />
                </div>
            </div>
            <div className="carousel__indicators" role="listbox" aria-label="carousel-indicators">
                {limitedItems.length > 1 &&
                    limitedItems.map((_item, index) => {
                        return (
                            <div
                                className="carousel__indicators__item"
                                key={`carousel-indicator-${index}`}
                                role="option"
                                aria-selected={index == activeStep}
                                data-active={index == activeStep ? '' : undefined}
                                onClick={() => {
                                    setCanAutoPlay(false);
                                    setActiveStep(index);
                                }}
                            ></div>
                        );
                    })}
            </div>
        </div>
    );
}

const getTriggerVariation = (showTriggers: boolean, showTriggersOnHover: boolean): string => {
    if (showTriggers) {
        return 'show-triggers';
    }

    if (showTriggersOnHover) {
        return 'show-triggers-on-hover';
    }

    return '';
};

const shouldHaveTriggers = (
    showTriggers: boolean,
    showTriggersOnHover: boolean,
    itemsLength: number
) => {
    return (showTriggers || showTriggersOnHover) && itemsLength > 1;
};
